// Vroom · entry point
//
// Wires up:
//   - Phaser game (single FireScene for the demo, only visible in game mode)
//   - VideoMode controller (default landing + fullscreen player)
//   - WebSocket → Card taps + mode_changed → routed by current mode
//   - Mode HUD + dev tap pad (synthesizes a Card and re-uses the real flow)

import Phaser from "phaser";
import { FireScene } from "./scenes/FireScene";
import { vehicleById, VEHICLES, type VehicleId, type VehicleDef } from "./vehicles";
import { API_BASE, WS_URL } from "./config";
import { sfx } from "./sfx";
import type { Card, Mode, ServerMsg } from "./types";

// Detect TV embed BEFORE Phaser is constructed so we can pick the renderer
// that works on that platform. Bravia ATV2's WebView reports WebGL as
// available but the resulting canvas is often invisible — fall back to
// CPU/Canvas rendering, which paints normally.
const isEmbedTv = new URLSearchParams(location.search).get("embed") === "tv";
if (isEmbedTv) document.body.classList.add("embed-tv");

// =============================================================================
// Phaser
// =============================================================================
const game = new Phaser.Game({
  type: isEmbedTv ? Phaser.CANVAS : Phaser.AUTO,
  parent: "phaser",
  backgroundColor: "#0b1426",
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: 1920,
    height: 1080,
  },
  scene: [FireScene],
});

let fireScene: FireScene | null = null;
function attachSceneCallbacks() {
  fireScene = game.scene.getScene("FireScene") as FireScene;
  fireScene.onSceneEnd = () => {
    game.scene.stop("FireScene");
    game.scene.start("FireScene");
    setTimeout(attachSceneCallbacks, 0);
  };
}
game.events.on("ready", attachSceneCallbacks);

// =============================================================================
// HUD
// =============================================================================
const connPill = document.getElementById("conn-pill")!;
const connText = document.getElementById("conn-text")!;
const modeText = document.getElementById("mode-text")!;
const modeBtn  = document.getElementById("mode-toggle") as HTMLButtonElement;

let currentMode: Mode = "video";

function setModeUi(m: Mode) {
  if (currentMode === m) return;
  currentMode = m;
  modeText.textContent = m;
  document.body.dataset.mode = m;
  if (m === "video") {
    // entering video mode: stop any playing video and show landing
    videoMode.showDefault();
  } else {
    // entering game mode: stop video to release resources
    videoMode.stop();
  }
}

modeBtn.addEventListener("click", async () => {
  const next: Mode = currentMode === "video" ? "game" : "video";
  try {
    await fetch(`${API_BASE}/v1/mode`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mode: next }),
    });
  } catch (e) {
    console.warn("mode toggle failed", e);
  }
});

// =============================================================================
// VideoMode controller
// =============================================================================
class VideoModeController {
  private playerEl  = document.getElementById("video-player")!;
  private defaultEl = document.getElementById("video-default")!;
  private videoEl   = document.getElementById("video-el") as HTMLVideoElement;
  private titleEl   = document.getElementById("video-title")!;
  private currentSrc: string | null = null;

  constructor() {
    this.videoEl.addEventListener("ended", () => this.showDefault());
    // keep <video> from emitting native fullscreen UI; we handle layout
    this.videoEl.controls = false;
  }

  // Show fullscreen video. If already playing the same src → no-op.
  // If playing a different src → swap immediately.
  play(url: string, title: string) {
    this.titleEl.innerHTML = `▶ ${escapeHtml(title)}`;
    // re-trigger the title-bar fade-in animation by reflow
    this.titleEl.style.animation = "none";
    void this.titleEl.offsetWidth;
    this.titleEl.style.animation = "";

    if (this.currentSrc !== url) {
      this.currentSrc = url;
      this.videoEl.src = url;
    }
    this.playerEl.classList.add("on");
    this.defaultEl.style.display = "none";
    this.videoEl.currentTime = 0;
    this.videoEl.play().catch(err => {
      console.warn("video play() rejected — likely autoplay policy:", err);
      toast("Tap once to enable sound");
    });
  }

  stop() {
    this.videoEl.pause();
    this.videoEl.removeAttribute("src");
    this.videoEl.load();      // clear pipeline
    this.currentSrc = null;
    this.playerEl.classList.remove("on");
  }

  showDefault() {
    this.stop();
    this.defaultEl.style.display = "flex";
  }
}
const videoMode = new VideoModeController();

// =============================================================================
// Tap routing (handles real worker taps + dev synthetic taps the same way)
// =============================================================================
function onCardTap(card: Card) {
  // Worker already toggled mode for mode_switch cards. mode_changed will follow.
  if (card.type === "mode_switch") return;

  if (currentMode === "video") {
    // In video mode: tap → play that card's video. Falls back to vehicle sample
    // if no video_url is configured.
    const url = card.video_url ?? sampleFor(card)?.sample_video;
    const title = displayName(card);
    if (url) videoMode.play(url, title);
    else toast(`No video set for ${title}`);
    return;
  }

  // Game mode
  if (card.type === "vehicle" && card.vehicle_id) {
    const def = vehicleById(card.vehicle_id);
    if (def) fireScene?.spawnVehicle(def);
    return;
  }
  // play-type cards in game mode are intentionally ignored for now.
}

function displayName(card: Card): string {
  if (card.title) return `${card.emoji ?? ""} ${card.title}`.trim();
  if (card.vehicle_id) {
    const v = (VEHICLES as Record<string, VehicleDef>)[card.vehicle_id];
    if (v) return `${v.emoji} ${v.label}`;
  }
  return card.uid;
}

function sampleFor(card: Card): VehicleDef | null {
  return card.vehicle_id ? vehicleById(card.vehicle_id) : null;
}

// =============================================================================
// Dev tap pad — full vehicle catalog, dispatched as if from worker
// =============================================================================
function buildDevPad() {
  const grid = document.getElementById("dev-pad-grid")!;
  for (const v of Object.values(VEHICLES)) {
    const b = document.createElement("button");
    b.textContent = `${v.emoji} ${v.label}`;
    b.dataset.vehicle = v.id;
    b.addEventListener("click", () => {
      // Synthesize a Card so the routing is identical to a real worker tap.
      onCardTap({
        uid:        `DEV:${v.id}`,
        type:       "vehicle",
        vehicle_id: v.id,
        title:      v.label,
        emoji:      v.emoji,
        enabled:    true,
      });
    });
    grid.appendChild(b);
  }
}
buildDevPad();

// =============================================================================
// Tile grid — clickable to mimic NFC tap (kid can also touch screen during dev)
// =============================================================================
function buildTileGrid() {
  const grid = document.getElementById("tile-grid")!;
  for (const v of Object.values(VEHICLES)) {
    const t = document.createElement("div");
    t.className = "tile";
    const visual = v.sprite_url
      ? `<img class="emo" src="${v.sprite_url}" alt="${escapeHtml(v.label)}" loading="lazy" />`
      : `<div class="emo">${v.emoji}</div>`;
    t.innerHTML = `${visual}<div class="lbl">${v.label}</div>`;
    t.addEventListener("click", () => {
      onCardTap({
        uid:        `DEV:${v.id}`,
        type:       "vehicle",
        vehicle_id: v.id,
        title:      v.label,
        emoji:      v.emoji,
        enabled:    true,
      });
    });
    grid.appendChild(t);
  }
}
buildTileGrid();

// =============================================================================
// WS
// =============================================================================
let ws: WebSocket | null = null;
let reconnectMs = 1000;

function connect() {
  ws = new WebSocket(WS_URL);
  ws.onopen = () => {
    connPill.classList.add("online");
    connText.textContent = "online";
    reconnectMs = 1000;
  };
  ws.onclose = () => {
    connPill.classList.remove("online");
    connText.textContent = `offline (retry ${reconnectMs}ms)`;
    setTimeout(connect, reconnectMs);
    reconnectMs = Math.min(reconnectMs * 2, 10_000);
  };
  ws.onerror = () => ws?.close();
  ws.onmessage = (ev) => {
    let msg: ServerMsg;
    try { msg = JSON.parse(ev.data); } catch { return; }
    onServerMsg(msg);
  };
}

function onServerMsg(msg: ServerMsg) {
  switch (msg.type) {
    case "hello":         setModeUi(msg.mode); break;
    case "mode_changed":  setModeUi(msg.mode); toast(`Mode → ${msg.mode}`); break;
    case "tap":           onCardTap(msg.card); break;
    case "tap_unknown":   toast(`unknown card: ${msg.uid}`); break;
  }
}

// =============================================================================
// Helpers
// =============================================================================
function toast(text: string) {
  const el = document.createElement("div");
  el.className = "toast";
  el.textContent = text;
  document.getElementById("app")!.appendChild(el);
  setTimeout(() => el.remove(), 1800);
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, ch => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]!
  ));
}

// `isEmbedTv` is computed at top of file (before Phaser is built).
// For embed=tv, also start in game mode so the WebView never shows the
// video grid splash before the WS hello flips us over.
if (isEmbedTv) {
  currentMode = "game";
}

// =============================================================================
// boot
// =============================================================================
console.log(`[vroom] API=${API_BASE} WS=${WS_URL} embed=${isEmbedTv ? "tv" : "no"} initialMode=${currentMode}`);
document.body.dataset.mode = currentMode;

// Periodic Phaser status snapshot — confirms render loop is alive and tells
// us the actual canvas size on the host (TV WebView vs browser).
setTimeout(() => {
  const canvas = (game as any).canvas as HTMLCanvasElement | undefined;
  const parent = canvas?.parentElement as HTMLElement | undefined;
  const docEl = document.documentElement;
  console.log(
    `[viewport] window=${window.innerWidth}x${window.innerHeight} ` +
    `docEl=${docEl.clientWidth}x${docEl.clientHeight} ` +
    `body=${document.body.offsetWidth}x${document.body.offsetHeight} ` +
    `phaser=${parent?.offsetWidth}x${parent?.offsetHeight} ` +
    `canvas=${canvas?.width}x${canvas?.height} mode=${document.body.dataset.mode}`,
  );

  // Force the page DOM to match the actual WebView pixel dimensions, then
  // re-trigger Phaser's scale manager. On Bravia ATV2 the WebView ignores
  // <meta viewport> + 100vh and resolves to a tiny default viewport,
  // leaving #phaser at 0 height.
  const w = window.innerWidth  || docEl.clientWidth;
  const h = window.innerHeight || docEl.clientHeight;
  if (w > 0 && h > 0 && parent) {
    parent.style.width  = w + "px";
    parent.style.height = h + "px";
    document.body.style.width  = w + "px";
    document.body.style.height = h + "px";
    game.scale.refresh();
    console.log(`[viewport] forced parent ${w}x${h}, refreshed Phaser scale`);
  }
}, 1500);

// Bravia's WebView reports the #phaser container as 960×0 at boot, before
// CSS layout settles. Phaser.Scale.FIT measures the parent at construction
// time and never re-measures, so the game canvas ends up rendered at 0px
// tall (invisible). Watch the parent for size changes and refresh the scale
// manager whenever it changes.
const phaserParent = document.getElementById("phaser");
if (phaserParent && typeof ResizeObserver !== "undefined") {
  let lastW = 0, lastH = 0;
  new ResizeObserver(entries => {
    for (const e of entries) {
      const { width, height } = e.contentRect;
      if ((width !== lastW || height !== lastH) && width > 0 && height > 0) {
        lastW = width; lastH = height;
        console.log(`[phaser] parent resized to ${width}x${height} → scale.refresh()`);
        game.scale.refresh();
      }
    }
  }).observe(phaserParent);
}

if (!isEmbedTv) {
  videoMode.showDefault();
}

// Autoplay gate: a single click anywhere on the gate registers a user gesture
// for this document, after which video.play() with sound is allowed for the
// session. TV WebView already bypasses the policy via
// mediaPlaybackRequiresUserGesture=false, so we drop the gate there.
const autoplayGate = document.getElementById("autoplay-gate")!;
if (isEmbedTv) {
  autoplayGate.remove();
  // Bravia ATV2's WebView is configured with mediaPlaybackRequiresUserGesture
  // = false, so we can prime AudioContext immediately at boot. On regular
  // browsers we wait for the gate click below.
  sfx.unlock();
} else {
  autoplayGate.addEventListener("click", () => {
    sfx.unlock();
    autoplayGate.classList.add("hidden");
    setTimeout(() => autoplayGate.remove(), 250);
  }, { once: true });
}

connect();
